import React from "react";

const TylerTriggs = () => {
  return (
    <div className="scientia-container">
      <div className="tyler-triggs-page">
        <div className="hero-section tyler-triggs-hero">
          <div className="hero-content">
            <div className="profile-section">
              <div className="profile-image">
                <img
                  alt="Tyler Triggs"
                  className="headshot"
                  src="/static/img/TylerTriggs-headshot.png"
                />
              </div>
              <div className="profile-info">
                <h1 className="profile-name">Tyler Triggs</h1>
                <p className="profile-title">
                  Software Engineering Manager, GenAI | Product-Minded Technical
                  Lead
                </p>
                <div className="profile-links">
                  <a
                    className="profile-link linkedin"
                    href="https://www.linkedin.com/in/tylertriggs/"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <img
                      alt="LinkedIn"
                      className="profile-link-icon"
                      src="/static/img/linkedInBug-White.png"
                    />
                    LinkedIn
                  </a>
                  <a
                    className="profile-link github"
                    href="https://github.com/ttriggs"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <img
                      alt="GitHub"
                      className="profile-link-icon"
                      src="/static/img/github-mark-white.png"
                    />
                    GitHub
                  </a>
                  <a
                    className="profile-link"
                    download
                    href="/static/tyler-triggs-resume.pdf"
                  >
                    Download Resume
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="content-section">
          <div className="container">
            <div className="about-section">
              <h2>About Tyler</h2>
              <p className="about-text">
                Tyler is a software engineering manager and technical lead who
                turns ambiguous business problems into production-grade GenAI
                systems. At Caterpillar&apos;s Cat Digital he leads 13 globally
                distributed engineers across multiple concurrent workstreams
                &mdash; owning architecture, hiring, budgeting, and capacity
                planning while staying hands-on in the code.
              </p>
              <p className="about-text">
                His work spans executive-facing NL-to-SQL analytics, multi-agent
                dealer assistants, and reusable application platforms that let
                teams stand up GenAI prototypes in days instead of weeks. Before
                management, he spent years shipping high-reliability
                transactional systems &mdash; multi-currency checkout, payments,
                and fraud prevention at{" "}
                <a
                  className="inline-link"
                  href="https://reverb.com/"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Reverb
                </a>
                &apos;s marketplace scale.
              </p>
              <p className="about-text">
                He is seeking engineering leadership and GenAI manager roles
                where he can help teams turn advanced AI capabilities into
                dependable, high-value products.
              </p>
            </div>

            <div className="resume-section">
              <h2>GenAI Engineering Leadership</h2>
              <p className="scientia-intro">
                As Software Engineering Manager for GenAI Applications at Cat
                Digital, Tyler owns delivery across 3+ concurrent workstreams
                and 13 engineers &mdash; and built a template full-stack
                application now used by 9+ internal projects across 4+ teams to
                eliminate repeated infrastructure, UI, and UX effort.
              </p>

              <div className="scientia-features">
                <div className="feature-group">
                  <h3>
                    <span aria-label="bar chart" role="img">
                      📊
                    </span>{" "}
                    Executive Financial-Metrics AI Analyst
                  </h3>
                  <p>
                    Architected and shipped NL-to-SQL over 60+ restricted-access
                    financial metrics, clearing security review with 10+
                    platform teams to reach production. Grounded in the
                    executive&apos;s live Power BI view &mdash; inheriting
                    current filters and time range &mdash; to help leaders plan,
                    forecast, and reflect on Caterpillar&apos;s most
                    confidential metrics.
                  </p>
                </div>

                <div className="feature-group">
                  <h3>
                    <span aria-label="robot" role="img">
                      🤖
                    </span>{" "}
                    Multi-Agent Dealer Assistant
                  </h3>
                  <p>
                    Led a cross-functional team of four to deliver a multi-agent
                    AI assistant pilot for 8 Caterpillar dealers, live in 4
                    weeks. Combined an owner&apos;s-manual RAG pipeline, cached
                    AI summaries, and brand-aware translation to help dealer
                    reps understand and anticipate customer needs.
                  </p>
                </div>

                <div className="feature-group">
                  <h3>
                    <span aria-label="lightning" role="img">
                      ⚡
                    </span>{" "}
                    Self-Improving Onboarding Agent
                  </h3>
                  <p>
                    Built self-improving tooling with automated knowledge-base
                    creation, best-practice reinforcement, governance, and
                    source-of-truth conflict resolution &mdash; cutting project
                    onboarding from a full day to under an hour.
                  </p>
                </div>

                <div className="feature-group">
                  <h3>
                    <span aria-label="bricks" role="img">
                      🧱
                    </span>{" "}
                    Ray-on-EMR Compute Platform
                  </h3>
                  <p>
                    Built a control-plane system for job observability and cost
                    attribution, reducing over-provisioning waste while enabling
                    compliant, autoscaling pipelines for data science teams.
                  </p>
                </div>
              </div>
            </div>

            <div className="scientia-section">
              <h2>Scientia &mdash; A Live GenAI Product</h2>
              <p className="scientia-intro">
                Scientia is a working product you can explore right now &mdash;
                Tyler&apos;s from-scratch demonstration of proactive,
                personalized AI: reliable backend systems, multi-step LLM
                orchestration, structured data workflows, and a polished
                real-time interface.
              </p>

              <div className="scientia-features">
                <div className="feature-group">
                  <h3>
                    <span aria-label="brain" role="img">
                      🧠
                    </span>{" "}
                    Adaptive Knowledge Modeling
                  </h3>
                  <p>
                    Scientia maintains personalized user facts and preferences
                    over time, combining human input with automated inference to
                    keep models current and contextually relevant.
                  </p>
                </div>

                <div className="feature-group">
                  <h3>
                    <span aria-label="calendar" role="img">
                      📅
                    </span>{" "}
                    Context-Aware Daily Insights
                  </h3>
                  <p>
                    The product synthesizes weather, calendar, and web-sourced
                    data to deliver daily messages that feel proactively helpful
                    rather than generic.
                  </p>
                </div>

                <div className="feature-group">
                  <h3>
                    <span aria-label="magnifying glass" role="img">
                      🔍
                    </span>{" "}
                    Research-Driven Interactions
                  </h3>
                  <p>
                    Users can ask for targeted research, adjust preferences, or
                    explore new topics through natural conversation backed by
                    structured AI workflows.
                  </p>
                </div>
              </div>
            </div>

            <div className="resume-section">
              <h2>Core Technical Capabilities</h2>
              <div className="tech-grid">
                <div className="tech-category">
                  <h4>AI &amp; GenAI</h4>
                  <ul>
                    <li>LangGraph &amp; LangChain</li>
                    <li>RAG &amp; NL-to-SQL</li>
                    <li>Multi-agent architecture</li>
                    <li>Langfuse &amp; Azure AI Foundry</li>
                  </ul>
                </div>
                <div className="tech-category">
                  <h4>Backend &amp; Data</h4>
                  <ul>
                    <li>FastAPI &amp; Django</li>
                    <li>PostgreSQL &amp; Snowflake</li>
                    <li>Redis &amp; Elasticsearch</li>
                    <li>Celery &amp; RabbitMQ</li>
                  </ul>
                </div>
                <div className="tech-category">
                  <h4>Cloud &amp; Platform</h4>
                  <ul>
                    <li>AWS (EMR, Lambda, Fargate, S3)</li>
                    <li>Docker &amp; Kubernetes</li>
                    <li>Ray &amp; Airflow</li>
                    <li>Datadog observability</li>
                  </ul>
                </div>
                <div className="tech-category">
                  <h4>Leadership &amp; Delivery</h4>
                  <ul>
                    <li>Team leadership &amp; hiring</li>
                    <li>Architecture reviews</li>
                    <li>AI governance</li>
                    <li>Executive stakeholder alignment</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="resume-section">
              <h2>Earlier Track Record</h2>
              <div className="scientia-features">
                <div className="feature-group">
                  <h3>Reverb.com &mdash; Senior Software Engineer</h3>
                  <p>
                    Led a team of four on high-visibility marketplace projects
                    including Gear Collections and Trade-In for Cash, and
                    delivered zero-downtime multi-currency checkout, seller
                    payouts, and billing using best-practice locking and
                    transactional rollback. Built fraud, KYC, and bad-actor
                    defenses, and served as domain expert for checkout, taxes,
                    and shipping.
                  </p>
                </div>

                <div className="feature-group">
                  <h3>New Era ADR &mdash; Senior Software Engineer</h3>
                  <p>
                    Originated a context-aware case-management UI/UX overhaul
                    that boosted satisfaction while cutting manual customer
                    support in half, and built a multi-party file-permissioning
                    framework with AWS-backed idempotent streaming uploads and
                    zip exports.
                  </p>
                </div>
              </div>
            </div>

            <div className="contact-section">
              <h2>Let&apos;s Connect</h2>
              <p className="contact-text">
                Interested in engineering leadership, GenAI product work, or
                collaborative opportunities? Download the resume for the
                complete picture, then reach out to continue the conversation.
              </p>
              <div className="contact-methods">
                <a
                  className="contact-button"
                  download
                  href="/static/tyler-triggs-resume.pdf"
                >
                  Download Resume
                </a>
                <a
                  className="contact-button contact-button--secondary"
                  href="https://www.linkedin.com/in/tylertriggs/"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Connect on LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TylerTriggs;
